"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { useGlobalPlayer } from "@/context/global-player-context";

/*
 * The player sheet (with framer-motion, the waveform and hls.js) is only
 * mounted once something plays, and its code is fetched when the browser is
 * idle after load, off the critical path but ready before the first play
 * (browsers only allow audio to start soon after a click). After the first
 * play it stays mounted, keeping its audio and animations.
 */
const loadSheet = () =>
	import("@/components/global-music-player/global-player-sheet");

const GlobalPlayerSheet = dynamic(
	() => loadSheet().then((module) => module.GlobalPlayerSheet),
	{ ssr: false },
);

export function GlobalPlayerSheetLazy({
	isAuthenticated,
}: {
	isAuthenticated: boolean;
}) {
	const { submission, isOpen } = useGlobalPlayer();
	const [needed, setNeeded] = useState(false);

	useEffect(() => {
		if (isOpen && submission) setNeeded(true);
	}, [isOpen, submission]);

	useEffect(() => {
		const prefetch = () => void loadSheet();
		if ("requestIdleCallback" in window) {
			const id = window.requestIdleCallback(prefetch, { timeout: 4000 });
			return () => window.cancelIdleCallback(id);
		}
		const id = setTimeout(prefetch, 2000);
		return () => clearTimeout(id);
	}, []);

	return needed ? (
		<GlobalPlayerSheet isAuthenticated={isAuthenticated} />
	) : null;
}
